import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Star, Search as SearchIcon, Coins, Download, UserX } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { BusinessFields, cleanBiz } from "@/pages/Register";
import { formatDate, formatEur, formatCoins } from "@/lib/format";
import { LedgerList } from "@/pages/account/Orders";

const Empty = ({ text, testid }) => <p className="rounded-2xl bg-[#F8F9FC] px-5 py-10 text-center text-sm text-[#64748B]" data-testid={testid}>{text}</p>;

export const ProfileTab = () => {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", city: user.city || "", bio: user.bio || "" });
  const [type, setType] = useState(user.account_type || "private");
  const [biz, setBiz] = useState(user.business || {});
  const [consent, setConsent] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, phone: f.phone || null, city: f.city || null, bio: f.bio || null, account_type: type, business: type === "business" ? cleanBiz(biz) : null };
      if (user.needs_onboarding && consent) Object.assign(body, { accept_terms: true, accept_privacy: true });
      const { data } = await api.patch("/account/profile", body);
      setUser(data);
      toast.success(t("account.saved"));
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  const inp = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), className: "field", "data-testid": `profile-${k}-input` });
  return (
    <form onSubmit={save} className="space-y-4" data-testid="profile-tab">
      {user.needs_onboarding && (
        <label className="flex gap-2.5 rounded-xl bg-[#FFF0EE] p-4 text-sm" data-testid="onboarding-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#3B1272]" data-testid="onboarding-consent-checkbox" />
          <span>{t("account.onboarding")} <Link to="/informative" className="underline">↗</Link></span>
        </label>
      )}
      <p className="text-sm text-[#64748B]">{user.email} {user.email_verified ? "✓" : ""}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className="label">{t("auth.firstName")}</label><input required {...inp("first_name")} /></div>
        <div><label className="label">{t("auth.lastName")}</label><input required {...inp("last_name")} /></div>
        <div><label className="label">{t("auth.phone")}</label><input {...inp("phone")} /></div>
        <div><label className="label">{t("auth.city")}</label><input {...inp("city")} /></div>
      </div>
      <div><label className="label">{t("account.bio")}</label><textarea maxLength={500} {...inp("bio")} className="field min-h-[80px]" /></div>
      <div><label className="label">{t("account.accountType")}</label>
        <select className="field" value={type} onChange={(e) => setType(e.target.value)} data-testid="profile-account-type">
          <option value="private">{t("auth.private")}</option><option value="business">{t("auth.business")}</option>
        </select></div>
      {type === "business" && <BusinessFields biz={biz} setBiz={setBiz} t={t} />}
      <button className="btn-violet" data-testid="profile-save-btn">{t("account.save")}</button>
    </form>
  );
};

const emptyAddr = { label: "Casa", full_name: "", street: "", city: "", zip_code: "", province: "", phone: "", is_default: false };

export const AddressesTab = () => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["addresses"], queryFn: async () => (await api.get("/account/addresses")).data });
  const [f, setF] = useState(emptyAddr);
  const [editId, setEditId] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, phone: f.phone || null };
      editId ? await api.put(`/account/addresses/${editId}`, body) : await api.post("/account/addresses", body);
      setF(emptyAddr); setEditId(null);
      qc.invalidateQueries({ queryKey: ["addresses"] });
      toast.success(t("account.saved"));
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const del = async (id) => { await api.delete(`/account/addresses/${id}`); qc.invalidateQueries({ queryKey: ["addresses"] }); };
  const inp = (k, extra = {}) => ({ value: f[k] ?? "", onChange: (e) => setF({ ...f, [k]: e.target.value }), className: "field", required: k !== "phone", "data-testid": `address-${k}-input`, ...extra });
  return (
    <div className="space-y-6" data-testid="addresses-tab">
      {data.length === 0 ? <Empty text={t("account.noAddresses")} testid="addresses-empty" /> : (
        <div className="grid gap-3 sm:grid-cols-2">{data.map((a) => (
          <div key={a.address_id} className="rounded-2xl border border-[#EEF0F6] p-4 text-sm" data-testid={`address-${a.address_id}`}>
            <p className="font-bold">{a.label} {a.is_default && <span className="pill ml-1 bg-[#EFE9FB] text-[#3B1272]"><Star className="h-3 w-3" />{t("account.default")}</span>}</p>
            <p className="mt-1 text-[#64748B]">{a.full_name}<br />{a.street}<br />{a.zip_code} {a.city} ({a.province})</p>
            <div className="mt-3 flex gap-3 text-xs font-semibold">
              <button onClick={() => { setEditId(a.address_id); setF({ ...a, phone: a.phone || "" }); }} className="text-[#3B1272]" data-testid={`address-edit-${a.address_id}`}>{t("account.edit")}</button>
              <button onClick={() => del(a.address_id)} className="text-red-600" data-testid={`address-delete-${a.address_id}`}>{t("account.delete")}</button>
            </div>
          </div>))}</div>
      )}
      <form onSubmit={submit} className="space-y-3 rounded-2xl bg-[#F8F9FC] p-5" data-testid="address-form">
        <p className="font-display font-bold">{editId ? t("account.edit") : t("account.addAddress")}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input placeholder={t("account.label")} {...inp("label")} />
          <input placeholder={t("account.fullName")} {...inp("full_name")} />
          <input placeholder={t("account.street")} {...inp("street")} />
          <input placeholder={t("auth.city")} {...inp("city")} />
          <input placeholder={t("auth.zip")} pattern="\d{5}" maxLength={5} {...inp("zip_code")} />
          <input placeholder={t("auth.province")} maxLength={2} {...inp("province")} />
          <input placeholder={t("auth.phone")} {...inp("phone")} />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} className="accent-[#3B1272]" data-testid="address-default-checkbox" />{t("account.setDefault")}</label>
        <button className="btn-violet" data-testid="address-save-btn">{t("account.save")}</button>
      </form>
    </div>
  );
};

export const FavoritesTab = () => {
  const { t } = useI18n();
  const { favIds } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["favorites", favIds.size], queryFn: async () => (await api.get("/favorites")).data });
  if (isLoading) return null;
  return data.length === 0 ? <Empty text={t("account.noFavorites")} testid="favorites-empty" /> : (
    <div className="grid grid-cols-2 gap-4 xl:grid-cols-3" data-testid="favorites-tab">{data.map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>
  );
};

export const SearchesTab = () => {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["saved-searches"], queryFn: async () => (await api.get("/saved-searches")).data });
  const del = async (id) => { await api.delete(`/saved-searches/${id}`); qc.invalidateQueries({ queryKey: ["saved-searches"] }); };
  return data.length === 0 ? <Empty text={t("account.noSearches")} testid="searches-empty" /> : (
    <div className="space-y-3" data-testid="searches-tab">{data.map((s) => (
      <div key={s.search_id} className="flex items-center justify-between gap-3 rounded-2xl border border-[#EEF0F6] p-4">
        <div><p className="flex items-center gap-2 font-semibold"><SearchIcon className="h-4 w-4 text-[#3B1272]" />{s.name}</p>
          <p className="mt-1 text-xs text-[#94A3B8]">{Object.entries(s.params).map(([k, v]) => `${k}: ${v}`).join(" · ")} — {formatDate(s.created_at, locale)}</p></div>
        <div className="flex gap-2">
          <Link to={`/cerca?${new URLSearchParams(s.params)}`} className="btn-violet !px-4 !py-1.5 text-sm" data-testid={`search-open-${s.search_id}`}>{t("account.open")}</Link>
          <button onClick={() => del(s.search_id)} className="rounded-full p-2 text-red-600 hover:bg-red-50" data-testid={`search-delete-${s.search_id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>))}</div>
  );
};

export const WalletTab = () => {
  const { t, locale } = useI18n();
  const { data } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/coins/wallet")).data });
  if (!data) return null;
  const r = data.rules;
  return (
    <div className="space-y-5" data-testid="wallet-tab">
      <div className="rounded-3xl bg-gradient-to-br from-[#3B1272] to-[#581C9C] p-7 text-white">
        <p className="flex items-center gap-2 text-sm text-white/70"><Coins className="h-4 w-4 text-amber-300" />{t("account.wallet.available")}</p>
        <p className="mt-2 font-display text-3xl font-extrabold sm:text-4xl" data-testid="wallet-balance">{formatCoins(data.available, locale)} Juppo Coin — {t("account.wallet.value")} {formatEur(data.value_cents, locale)}</p>
        <div className="mt-5 flex gap-6 text-sm"><span>{t("account.wallet.pending")}: <b data-testid="wallet-pending">{data.pending}</b></span><span>{t("account.wallet.reserved")}: <b data-testid="wallet-reserved">{data.reserved}</b></span></div>
      </div>
      <div className="rounded-2xl bg-amber-50 p-5 text-sm text-amber-900" data-testid="wallet-rules">
        <p className="font-bold">{t("account.wallet.rules")}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>1 Coin = {formatEur(r.coin_value_cents, locale)} · 1 Coin / {r.earn_eur_per_coin} € · 100 Coin = {formatEur(100 * r.coin_value_cents, locale)}</li>
          <li>{t("home.coinText")}</li><li>{t("home.coinExample")}</li><li>{t("home.coinNote")}</li>
        </ul>
      </div>
      {data.debt > 0 && <p className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="wallet-debt">{t("p3.wallet.debt")}: {data.debt}</p>}
      <p className="font-display font-bold">{t("account.wallet.history")}</p>
      {data.entries.length === 0 ? <Empty text={t("account.wallet.empty")} testid="wallet-empty" /> : <LedgerList entries={data.entries} t={t} locale={locale} />}
    </div>
  );
};

export const NotificationsTab = () => {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [p, setP] = useState(user.notification_prefs || { messages: true, offers: true, price_drops: true, orders: true, marketing: false });
  const save = async () => {
    try { const { data } = await api.put("/account/notifications", p); setUser({ ...user, notification_prefs: data }); toast.success(t("account.saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="notifications-tab">
      {Object.keys(p).map((k) => (
        <label key={k} className="flex items-center justify-between rounded-2xl border border-[#EEF0F6] px-4 py-3.5 text-sm font-semibold">{t(`account.notif.${k}`)}
          <input type="checkbox" checked={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.checked })} className="h-5 w-5 accent-[#3B1272]" data-testid={`notif-${k}-toggle`} /></label>
      ))}
      <button onClick={save} className="btn-violet" data-testid="notifications-save-btn">{t("account.save")}</button>
    </div>
  );
};

export const PrivacyTab = () => {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["privacy"], queryFn: async () => (await api.get("/account/privacy-requests")).data });
  const exportData = async () => {
    try {
      const { data: d } = await api.get("/account/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }));
      const a = document.createElement("a"); a.href = url; a.download = "juppo-dati.json"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const requestDeletion = async () => {
    if (!window.confirm(t("account.confirmDeletion"))) return;
    try { await api.post("/account/privacy-requests", { type: "deletion" }); qc.invalidateQueries({ queryKey: ["privacy"] }); toast.success(t("account.requestSent")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="privacy-tab">
      <div className="rounded-2xl border border-[#EEF0F6] p-5"><p className="font-semibold">{t("account.privacyExport")}</p><p className="mt-1 text-sm text-[#64748B]">{t("account.privacyExportDesc")}</p>
        <button onClick={exportData} className="btn-violet mt-3" data-testid="export-data-btn"><Download className="h-4 w-4" />{t("account.privacyExport")}</button></div>
      <div className="rounded-2xl border border-red-100 p-5"><p className="font-semibold text-red-700">{t("account.requestDeletion")}</p><p className="mt-1 text-sm text-[#64748B]">{t("account.deletionDesc")}</p>
        <button onClick={requestDeletion} className="mt-3 rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50" data-testid="request-deletion-btn"><UserX className="mr-1 inline h-4 w-4" />{t("account.requestDeletion")}</button></div>
      {data.length > 0 && <div><p className="label">{t("account.requests")}</p>{data.map((r) => <p key={r.request_id} className="text-sm" data-testid={`privacy-request-${r.request_id}`}>{r.type} · {r.status} · {formatDate(r.created_at, locale, true)}</p>)}</div>}
    </div>
  );
};

export const BlockedTab = () => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["blocks"], queryFn: async () => (await api.get("/blocks")).data });
  const unblock = async (id) => { await api.delete(`/blocks/${id}`); qc.invalidateQueries({ queryKey: ["blocks"] }); };
  return data.length === 0 ? <Empty text={t("account.noBlocked")} testid="blocked-empty" /> : (
    <div className="space-y-2" data-testid="blocked-tab">{data.map((u) => (
      <div key={u.user_id} className="flex items-center justify-between rounded-2xl border border-[#EEF0F6] px-4 py-3"><span className="font-semibold">{u.name}</span>
        <button onClick={() => unblock(u.user_id)} className="text-sm font-semibold text-[#3B1272]" data-testid={`unblock-${u.user_id}`}>{t("account.unblock")}</button></div>))}</div>
  );
};

export const ComingTab = ({ text }) => {
  const { t } = useI18n();
  return <div className="rounded-2xl bg-[#EFE9FB] px-6 py-12 text-center" data-testid="coming-tab"><p className="font-display text-lg font-bold text-[#3B1272]">{t("account.coming")}</p><p className="mt-1 text-sm text-[#4B4763]">{text}</p></div>;
};
